import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { trackedArray } from '@ember/reactive/collections';
import Checkbox from 'tio-reimagined/components/checkbox';
import Button from 'tio-reimagined/components/button';
import { VStack } from 'tio-ui/components/layout';
import { contains } from 'tio-ui/utilities';
import TioDialogDocumentAsset from 'tio-common/components/tio/dialog/document-asset';
import type ConditionModel from 'tio-data/models/condition';
import type IntlService from 'ember-intl/services/intl';
import type PostLoginService from 'tio-employee/services/post-login';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from '@ember/routing/router-service';

export default class PostLoginTermsTemplate extends Component {
  @service declare postLogin: PostLoginService;
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked didAgreeAppTermsAndPrivacy = false;
  @tracked showingTermsOfUse = false;
  @tracked showingPrivacyPolicy = false;
  @tracked errorMessage = '';

  acceptedCustomTerms: ConditionModel[] = trackedArray([]);

  get termsOfUse() {
    return this.preRegister.termsOfUse;
  }

  get privacyPolicy() {
    return this.preRegister.privacyPolicy;
  }

  get customTerms() {
    return this.preRegister.customTerms || [];
  }

  get didAcceptAllCustomTerms() {
    return this.acceptedCustomTerms.length === this.customTerms.length;
  }

  get didAcceptAllTerms() {
    return this.didAgreeAppTermsAndPrivacy && this.didAcceptAllCustomTerms;
  }

  setDidAgreeAppTermsAndPrivacy = (value: boolean) => {
    this.didAgreeAppTermsAndPrivacy = value;
  };

  closePrivacyPolicy = () => {
    this.showingPrivacyPolicy = false;
  };

  closeTermsOfUse = () => {
    this.showingTermsOfUse = false;
  };

  didChangeAcceptanceOfCustomTerm = (condition: ConditionModel, value: boolean) => {
    if (value) {
      this.acceptedCustomTerms.push(condition);
    } else {
      const index = this.acceptedCustomTerms.indexOf(condition);
      if (index !== -1) {
        this.acceptedCustomTerms.splice(index, 1);
      }
    }
  };

  showDialog = (type: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    switch (type) {
      case 'terms':
        this.showingTermsOfUse = true;
        break;
      case 'privacy':
        this.showingPrivacyPolicy = true;
        break;
    }
  };

  submitTask = dropTask(async () => {
    this.errorMessage = '';
    if (!this.didAcceptAllTerms) {
      this.errorMessage = this.intl.t('login.terms_accept_error');
      return;
    }

    try {
      await this.postLogin.acceptTerms();
      const nextRoute = this.postLogin.nextRouteName;
      if (nextRoute) {
        this.router.transitionTo(nextRoute);
        return;
      }
      const accessToken = await this.postLogin.complete();
      await this.postLogin.completeAuthentication(accessToken);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.intl.t('login.terms_accept_error');
      this.errorMessage = message;
    }
  });

  <template>
    <div class="min-h-screen bg-gray-50 flex items-center justify-center">
      <div class="w-full max-w-2xl bg-white rounded-lg shadow p-8">
        <VStack class="gap-y-6">
          <div class="text-center">
            <h1 class="text-2xl font-semibold">{{t "login.terms_of_service_heading"}}</h1>
            <p class="text-gray-600 mt-2">{{t "login.please_accept_the_terms"}}</p>
          </div>

          <div class="my-2">
            <div class="flex flex-row items-start my-2">
              <Checkbox
                @size="lg"
                @checked={{this.didAgreeAppTermsAndPrivacy}}
                @onChange={{this.setDidAgreeAppTermsAndPrivacy}}
                aria-labelledby="appTermsAndPrivacy"
              />
              <div id="appTermsAndPrivacy" class="ml-2">
                {{t "login.i_have_read_and_agree_to_tuitions"}}
                <button class="tio-copy" type="button" {{on "click" (fn this.showDialog "terms")}}>
                  {{t "login.terms_of_service"}}
                </button>
                {{t "and"}}
                <button
                  class="tio-copy"
                  type="button"
                  {{on "click" (fn this.showDialog "privacy")}}
                >
                  {{t "login.privacy_policy"}}
                </button>.
              </div>
            </div>

            {{#each this.customTerms as |condition|}}
              <div class="flex flex-row items-start my-2">
                <Checkbox
                  @size="lg"
                  @checked={{contains this.acceptedCustomTerms condition}}
                  @onChange={{fn this.didChangeAcceptanceOfCustomTerm condition}}
                  aria-labelledby={{condition.id}}
                />
                <div id={{condition.id}} class="ml-2 tio-terms-and-conditions">
                  {{htmlSafe condition.fullText}}
                </div>
              </div>
            {{/each}}
          </div>

          {{#if this.errorMessage}}
            <p class="text-red-600 text-sm" data-test-terms-error>{{this.errorMessage}}</p>
          {{/if}}

          <div class="flex justify-center">
            <Button
              @intent="primary"
              @isRunning={{this.submitTask.isRunning}}
              {{on "click" this.submitTask.perform}}
              data-test-terms-submit
            >
              {{t "login.agree_text"}}
            </Button>
          </div>
        </VStack>
      </div>
    </div>

    <TioDialogDocumentAsset
      @title={{t "login.privacy_policy"}}
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.privacyPolicy.staticJsonAssetName}}
      @show={{this.showingPrivacyPolicy}}
      @onCancel={{this.closePrivacyPolicy}}
    />

    <TioDialogDocumentAsset
      @title={{t "login.terms_of_service"}}
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.termsOfUse.staticJsonAssetName}}
      @show={{this.showingTermsOfUse}}
      @onCancel={{this.closeTermsOfUse}}
    />
  </template>
}
